import * as THREE from 'three';
import AIPilot from './AIPilot.js';
import { ENEMIES, ENEMY_MISSILE } from '../core/SimConfig.js';
import { clamp, rand, srand, DEG2RAD } from '../utils.js';

const _playerFwd = new THREE.Vector3();
const _launcherFwd = new THREE.Vector3();
// Scratch for the player's ground track — see _playerTrackDir.
const _track = { x: 0, z: 0, speed: 0 };

/**
 * Fixed-wing AI pilot.
 *
 *   - explore:      pick a random world waypoint, fly to it, repeat
 *   - follow:       trail the assigned leader (same offsets as the heli pilot)
 *   - attackPlayer: work into a slot ~preferredRange behind the player along
 *                   their velocity vector, but approach it on a randomised
 *                   flanking arc (offset to one side, usually perched above)
 *                   that converges into the six only at close range.
 *   - evade:        defensive break + extend, entered when the player holds
 *                   the MiG's rear quarter. Returns to whatever state it
 *                   interrupted, on a fresh flanking plan.
 *
 * Each tick sets the flight model's desiredHeading / desiredSpeed /
 * desiredVerticalRate / loadFactorLimit; the flight model handles motion.
 */
export default class FixedWingAIPilot extends AIPilot {
  constructor(params, initialState = 'explore') {
    super();
    this.params = params;
    this.state = initialState;

    this.waypoint = null;
    this._terrainQueryTimer = 0;
    this._groundHeight = 0;
    this._hasTerrainSample = false;

    this._threat = null;

    // Follow-the-leader state (mirrors HelicopterAIPilot)
    this.leader = null;
    this._followGap = 0;
    this._followLateral = 0;
    this._followAltOffset = 0;

    // Per-frame player reference (set by EnemyPlane.update)
    this._player = null;

    // Pilot reaction-time hesitation. >0 means we're mid-decision and should
    // hold the current course/speed until the timer expires.
    this._waypointReactionTimer = 0;
    this._pendingEngagePlayer = false;
    this._engageReactionTimer = 0;

    // Flanking approach plan (attackPlayer) — see _rollFlankPlan.
    this._flankSide = 0;               // -1 / +1, 0 = unplanned
    this._flankOffset = 0;             // m — lateral aim-off from the tail slot
    this._flankAlt = 0;                // m — perch offset above the player
    this._flankTimer = 0;              // s until the plan is re-rolled

    // Defensive-break state
    this._sixTimer = 0;                // s the player has held our rear quarter
    this._evadeCooldown = 0;           // s before another break is allowed
    this._evadePhase = 'break';        // 'break' | 'extend'
    this._evadeTimer = 0;
    this._breakHeading = 0;
    this._extendHeading = 0;
    this._evadeReturnState = initialState;
  }

  _randomReactionDelay() {
    const r = ENEMIES.reactionDelay;
    return rand(r.minDelay, r.maxDelay);
  }

  followLeader(leader) {
    const f = ENEMIES.formation;
    this.leader = leader;
    this._followGap = f.spacing + srand() * f.spacingJitter;
    this._followLateral = srand() * f.lateralJitter;
    this._followAltOffset = srand() * f.altitudeJitter;
    this.state = 'follow';
  }

  promoteToLeader() {
    this.leader = null;
    this.waypoint = null;
    // Mid-break the maneuver outranks the promotion — redirect where it lands.
    if (this.state === 'evade') { this._evadeReturnState = 'explore'; return; }
    this.state = 'explore';
  }

  /** Switch into the attack-on-player state, after a brief reaction delay. */
  engagePlayer() {
    if (this.state === 'attackPlayer' || this._pendingEngagePlayer) return;
    this._pendingEngagePlayer = true;
    this._engageReactionTimer = this._randomReactionDelay();
  }

  threaten(threat) {
    this._threat = threat;
  }

  update(dt, plane, terrainCollider, player = null, platform = null) {
    this._sampleTerrain(dt, plane, terrainCollider);
    this._player = player;
    this._platform = platform;

    // Turn authority is re-asserted every tick at the sustained g limit; only
    // the break raises it, and only while it is actually running.
    plane.loadFactorLimit = this.params.maxLoadFactor;

    if (this.leader && (this.leader.dead || this.leader.flight.structuralFailure)) {
      this.promoteToLeader();
    }

    if (this._pendingEngagePlayer) {
      this._engageReactionTimer -= dt;
      if (this._engageReactionTimer <= 0) {
        this._pendingEngagePlayer = false;
        this.leader = null;
        // Don't cut a break short to start attacking — queue it instead.
        if (this.state === 'evade') this._evadeReturnState = 'attackPlayer';
        else                        this.state = 'attackPlayer';
      }
    }

    if (this._waypointReactionTimer > 0) this._waypointReactionTimer -= dt;
    if (this._evadeCooldown > 0) this._evadeCooldown -= dt;

    if (this.state !== 'evade') this._checkEvadeTrigger(dt, plane, player);

    switch (this.state) {
      case 'follow':       this._tickFollow(plane); break;
      case 'attackPlayer': this._tickAttack(dt, plane); break;
      case 'evade':        this._tickEvade(dt, plane); break;
      case 'explore':
      default:             this._tickExplore(plane); break;
    }
  }

  // ── Terrain sampling — same pattern as the heli pilot ────────────────────
  _sampleTerrain(dt, plane, terrainCollider) {
    this._terrainQueryTimer -= dt;
    if (this._terrainQueryTimer > 0 || !terrainCollider) return;
    this._terrainQueryTimer = ENEMIES.terrainQueryInterval;

    const here = terrainCollider.getHeightAt(plane.position.x, plane.position.z);

    const lookDist = plane.forwardSpeed * ENEMIES.terrainLookaheadSeconds;
    const ax = plane.position.x - Math.sin(plane.heading) * lookDist;
    const az = plane.position.z - Math.cos(plane.heading) * lookDist;
    const ahead = terrainCollider.getHeightAt(ax, az);

    let h = null;
    if (here !== null && ahead !== null) h = Math.max(here, ahead);
    else if (here !== null) h = here;
    else if (ahead !== null) h = ahead;

    if (h !== null) {
      this._groundHeight = h;
      this._hasTerrainSample = true;
    }
  }

  // ── States ───────────────────────────────────────────────────────────────
  _tickExplore(plane) {
    const p = this.params;

    // Arriving at the waypoint clears it and starts a reaction timer; we then
    // hold the current heading and cruise until the pilot "decides" on the
    // next one. Without a waypoint at startup we pick one immediately.
    if (this.waypoint && this._distToWaypointXZ(plane) < ENEMIES.waypointReachRadius) {
      this.waypoint = null;
      this._waypointReactionTimer = this._randomReactionDelay();
    }
    if (!this.waypoint && this._waypointReactionTimer <= 0) {
      this.waypoint = this._randomWaypoint();
    }

    if (!this.waypoint) {
      plane.desiredSpeed = p.cruiseSpeed;
      plane.desiredVerticalRate = 0;
      return;
    }

    const dx = this.waypoint.x - plane.position.x;
    const dz = this.waypoint.z - plane.position.z;
    plane.desiredHeading = Math.atan2(-dx, -dz);
    plane.desiredSpeed = p.cruiseSpeed;

    let targetY;
    if (this._hasTerrainSample) {
      targetY = this._groundHeight + this.waypoint.altAGL;
    } else {
      targetY = plane.position.y;
    }
    const altErr = targetY - plane.position.y;
    plane.desiredVerticalRate = this._terrainSafeVR(plane, clamp(
      altErr * ENEMIES.altitudeP,
      -p.maxDescentRate, p.maxClimbRate,
    ));
  }

  _tickFollow(plane) {
    const p = this.params;
    const lf = this.leader.flight;

    const fwdX = -Math.sin(lf.heading);
    const fwdZ = -Math.cos(lf.heading);
    const rightX =  Math.cos(lf.heading);
    const rightZ = -Math.sin(lf.heading);

    const slotX = lf.position.x - fwdX * this._followGap + rightX * this._followLateral;
    const slotZ = lf.position.z - fwdZ * this._followGap + rightZ * this._followLateral;
    const slotY = lf.position.y + this._followAltOffset;

    const dx = slotX - plane.position.x;
    const dz = slotZ - plane.position.z;
    const distXZ = Math.sqrt(dx * dx + dz * dz);
    if (distXZ > 5) plane.desiredHeading = Math.atan2(-dx, -dz);
    else            plane.desiredHeading = lf.heading;

    const f = ENEMIES.formation;
    const longErr = dx * fwdX + dz * fwdZ;
    const speedDelta = clamp(longErr * f.distanceP, -f.maxSpeedDelta, f.maxSpeedDelta);
    plane.desiredSpeed = clamp(lf.forwardSpeed + speedDelta, p.minSpeed, p.maxSpeed);

    const altErr = slotY - plane.position.y;
    plane.desiredVerticalRate = this._terrainSafeVR(plane, clamp(
      altErr * ENEMIES.altitudeP, -p.maxDescentRate, p.maxClimbRate));
  }

  /**
   * Attack-the-player state. Works into a slot ~preferredRange behind the
   * player along their velocity vector (so we end up on the six), but does
   * not drive at it in a straight line: the current flanking plan aims off to
   * one side of the slot and perches above it, fading out as range closes.
   * Speed is closure-controlled — slow toward attackSpeed at the slot,
   * accelerate toward maxSpeed when far. Falls back to explore if no player
   * reference is available.
   */
  _tickAttack(dt, plane) {
    const p = this.params;
    const player = this._player;
    if (!player) { this._tickExplore(plane); return; }

    const a = ENEMIES.attack;
    const playerPos = player.position;
    const playerVel = player.velocity;

    this._flankTimer -= dt;
    if (this._flankTimer <= 0) this._rollFlankPlan();

    const track = this._playerTrackDir(player);
    const vx = track.x, vz = track.z;

    // Lead intercept: aim where the player will be in interceptLeadSec, then
    // back off preferredRange along their heading to land in the tail slot.
    const leadX = playerPos.x + playerVel.x * a.interceptLeadSec;
    const leadZ = playerPos.z + playerVel.z * a.interceptLeadSec;
    let slotX = leadX - vx * a.preferredRange;
    let slotZ = leadZ - vz * a.preferredRange;

    // Range to the player (not the slot) drives both the speed controller and
    // how much of the flanking plan is still in play.
    const pdx = playerPos.x - plane.position.x;
    const pdz = playerPos.z - plane.position.z;
    const distToPlayer = Math.sqrt(pdx * pdx + pdz * pdz);
    const blend = clamp(
      (distToPlayer - a.flankBlendNear) / (a.flankBlendFar - a.flankBlendNear),
      0, 1,
    );

    let dx = slotX - plane.position.x;
    let dz = slotZ - plane.position.z;

    // Flank: displace the aim point perpendicular to our own bearing to the
    // slot. Because the perpendicular is re-derived each frame, holding a
    // fixed offset traces a wide arc that converges on the slot rather than
    // orbiting it — and the offset shrinks to nothing inside flankBlendNear.
    if (blend > 0) {
      const len = Math.hypot(dx, dz) || 1;
      const off = this._flankOffset * blend * this._flankSide;
      slotX +=  (dz / len) * off;
      slotZ += -(dx / len) * off;
      dx = slotX - plane.position.x;
      dz = slotZ - plane.position.z;
    }

    // Heading toward the (flanked) slot. Once we're very close to it, mirror
    // the player's heading so we sit on their six instead of jittering.
    const distToSlot = Math.sqrt(dx * dx + dz * dz);
    if (distToSlot > 50) {
      plane.desiredHeading = Math.atan2(-dx, -dz);
    } else {
      plane.desiredHeading = Math.atan2(-vx, -vz);
    }

    // Speed: closure-controlled. When the player is far, we want to close
    // fast; when we're already on their six, match their speed and not overrun.
    const rangeErr = distToPlayer - a.preferredRange;
    const speedTarget = track.speed + rangeErr * a.speedClosureP;
    plane.desiredSpeed = clamp(speedTarget, p.attackSpeed, p.maxSpeed);

    // Altitude: P-track the player, offset by this plan's perch on the same
    // range blend, with terrain floor enforcement.
    const altErr = (playerPos.y + this._flankAlt * blend) - plane.position.y;
    plane.desiredVerticalRate = this._terrainSafeVR(plane, clamp(
      altErr * a.altitudeP, -p.maxDescentRate, p.maxClimbRate));

    this._maybeFireMissile(plane, player, playerPos, vx, vz);
  }

  /**
   * Roll a fresh flanking approach: which side to swing out to, how far, and
   * how high to perch. Called on a timer from _tickAttack and again whenever
   * an evasion ends, so the re-attack never retraces the run that just got
   * the MiG shot at.
   */
  _rollFlankPlan() {
    const a = ENEMIES.attack;
    if (this._flankSide === 0) this._flankSide = Math.random() < 0.5 ? 1 : -1;
    else if (Math.random() < a.flankSideFlipChance) this._flankSide = -this._flankSide;

    this._flankOffset = rand(a.flankOffset[0], a.flankOffset[1]);
    this._flankAlt = rand(a.flankAltOffset[0], a.flankAltOffset[1]);
    this._flankTimer = rand(a.flankRerollInterval[0], a.flankRerollInterval[1]);
  }

  // ── Evasion ──────────────────────────────────────────────────────────────
  /**
   * Defensive-break trigger. Looks for the player *settled* into our rear
   * quarter with their nose on us — the geometry of a gun or IR pass, not
   * merely a jet that happens to be behind. The condition must hold for
   * triggerHold seconds (a crossing pass sweeps through it too fast), and a
   * post-break cooldown stops a chased MiG from breaking continuously, which
   * would make it impossible to kill.
   */
  _checkEvadeTrigger(dt, plane, player) {
    const e = ENEMIES.evade;
    if (!player || this._evadeCooldown > 0) { this._sixTimer = 0; return; }

    const dx = player.position.x - plane.position.x;
    const dy = player.position.y - plane.position.y;
    const dz = player.position.z - plane.position.z;
    if (Math.sqrt(dx * dx + dy * dy + dz * dz) > e.detectRange) { this._sixTimer = 0; return; }

    const horiz = Math.hypot(dx, dz);
    if (horiz < 1) { this._sixTimer = 0; return; }
    const bx = dx / horiz, bz = dz / horiz;

    // 1) Player in our rear cone: the bearing to them sits more than
    //    (180° − rearConeDeg) off our nose.
    const fwdX = -Math.sin(plane.heading);
    const fwdZ = -Math.cos(plane.heading);
    if (fwdX * bx + fwdZ * bz > -Math.cos(e.rearConeDeg * DEG2RAD)) {
      this._sixTimer = 0;
      return;
    }

    // 2) ...and pointed at us: their nose is within noseOnDeg of the
    //    reciprocal bearing (player → MiG is −b).
    _playerFwd.set(0, 0, -1).applyQuaternion(player.quaternion);
    const pl = Math.hypot(_playerFwd.x, _playerFwd.z) || 1;
    const noseDot = (_playerFwd.x * -bx + _playerFwd.z * -bz) / pl;
    if (noseDot < Math.cos(e.noseOnDeg * DEG2RAD)) { this._sixTimer = 0; return; }

    this._sixTimer += dt;
    if (this._sixTimer >= e.triggerHold) this._beginBreak(plane, bx, bz);
  }

  /** Enter the break, choosing a side and a heading change. */
  _beginBreak(plane, bx, bz) {
    const e = ENEMIES.evade;
    if (this.state !== 'evade') this._evadeReturnState = this.state;
    this.state = 'evade';
    this._evadePhase = 'break';
    this._evadeTimer = rand(e.breakDuration[0], e.breakDuration[1]);
    this._sixTimer = 0;

    // Which side is the attacker offset to, in our body frame? Breaking into
    // them is the classic answer — it forces an overshoot — but doing it every
    // time would be its own pattern, so sometimes we break away instead.
    const rightX =  Math.cos(plane.heading);
    const rightZ = -Math.sin(plane.heading);
    const attackerSide = (bx * rightX + bz * rightZ) >= 0 ? 1 : -1;
    const side = Math.random() < e.breakIntoPlayerChance ? attackerSide : -attackerSide;

    this._breakHeading = plane.heading
      + side * rand(e.breakTurnDeg[0], e.breakTurnDeg[1]) * DEG2RAD;
  }

  /**
   * Break, then extend. The break is a descending turn at the airframe's
   * instantaneous g limit; the extend accelerates away to open the range
   * before re-attacking. Missile shots stay live throughout — a break that ends
   * nose-on to the player is exactly the head-on geometry _maybeFireMissile
   * is willing to take.
   */
  _tickEvade(dt, plane) {
    const p = this.params;
    const e = ENEMIES.evade;
    const player = this._player;
    this._evadeTimer -= dt;

    if (this._evadePhase === 'break') {
      // Pull to the instantaneous g limit and let the speed come back — a jet
      // cannot accelerate through a max-rate turn, and the flight model's turn
      // rate is inversely proportional to speed anyway, so commanding a dash
      // here would flatten the very turn it's meant to be making.
      plane.loadFactorLimit = p.breakLoadFactor;
      plane.desiredHeading = this._breakHeading;
      plane.desiredSpeed = p.breakSpeed;
      plane.desiredVerticalRate = this._terrainSafeVR(plane, -e.breakDescentRate);

      let headingErr = this._breakHeading - plane.heading;
      while (headingErr >  Math.PI) headingErr -= Math.PI * 2;
      while (headingErr < -Math.PI) headingErr += Math.PI * 2;
      if (this._evadeTimer <= 0 || Math.abs(headingErr) < e.breakCompleteDeg * DEG2RAD) {
        this._beginExtend(plane);
      }
    } else {
      plane.desiredHeading = this._extendHeading;
      plane.desiredSpeed = p.extendSpeed;

      // Shallow descent while accelerating away, levelling off well above the
      // hard floor so the extend doesn't finish in a valley.
      let vr = -e.extendDescentRate;
      if (this._hasTerrainSample
        && plane.position.y - this._groundHeight < e.extendMinAltAGL) vr = 0;
      plane.desiredVerticalRate = this._terrainSafeVR(plane, vr);

      const separated = !player || Math.hypot(
        player.position.x - plane.position.x,
        player.position.z - plane.position.z,
      ) > e.extendRange;
      if (this._evadeTimer <= 0 || separated) this._endEvade();
    }

    if (player) {
      const track = this._playerTrackDir(player);
      this._maybeFireMissile(plane, player, player.position, track.x, track.z);
    }
  }

  /** Pick the heading that actually opens the range, and commit to it. */
  _beginExtend(plane) {
    const e = ENEMIES.evade;
    this._evadePhase = 'extend';
    this._evadeTimer = rand(e.extendDuration[0], e.extendDuration[1]);

    // Straight away from the player is ideal, but if the break has already
    // carried us through the merge, turning to it would hand over our tail a
    // second time. Past extendMaxTurnDeg we hold what we have — the two jets
    // are diverging anyway — and let the geometry do the separating.
    this._extendHeading = plane.heading;
    const player = this._player;
    if (!player) return;

    const away = Math.atan2(
      -(plane.position.x - player.position.x),
      -(plane.position.z - player.position.z),
    );
    let turn = away - plane.heading;
    while (turn >  Math.PI) turn -= Math.PI * 2;
    while (turn < -Math.PI) turn += Math.PI * 2;
    if (Math.abs(turn) <= e.extendMaxTurnDeg * DEG2RAD) this._extendHeading = away;
  }

  /** Leave the evasion, on a fresh approach plan and a re-break cooldown. */
  _endEvade() {
    const e = ENEMIES.evade;
    this._evadeCooldown = rand(e.cooldown[0], e.cooldown[1]);
    this._sixTimer = 0;
    this._rollFlankPlan();

    if (this._evadeReturnState === 'follow' && !this.leader) this._evadeReturnState = 'explore';
    this.state = this._evadeReturnState || 'explore';
  }

  /**
   * Heat-seeker firing decision. Fires only when the geometry actually
   * favors a hit, which requires two independent conditions:
   *
   *   1. Seeker gimbal: target lies within ENEMY_MISSILE.gimbalLimitDeg of
   *      the launcher's body-forward axis. The seeker sits on the missile
   *      nose (which aligns with the launcher's nose at launch), so it
   *      can't acquire a target outside that cone. This rejects "mig flew
   *      past and is now facing away" out of the box.
   *   2. Aspect (player frame): the launcher sits inside the player's
   *      rear-quarter cone (tail chase) OR head-on cone (closing rate).
   *      Beam shots have poor intercept geometry and are filtered out.
   */
  _maybeFireMissile(plane, player, playerPos, vx, vz) {
    const platform = this._platform;
    if (!platform || !platform.canFireMissile) return;
    if (!player) return;

    // Bearing from launcher → player (3D for gimbal, horizontal for aspect).
    const lx = playerPos.x - plane.position.x;
    const ly = playerPos.y - plane.position.y;
    const lz = playerPos.z - plane.position.z;
    const range = Math.sqrt(lx * lx + ly * ly + lz * lz);
    if (range < ENEMY_MISSILE.minRange || range > ENEMY_MISSILE.maxRange) return;

    // 1) Seeker gimbal: angle between launcher body-forward and bearing
    //    to player, in 3D. -Z is the model's forward convention (matches
    //    FixedWingFlightModel's quaternion composition).
    _launcherFwd.set(0, 0, -1).applyQuaternion(plane.quaternion);
    const gimbalDot = clamp((_launcherFwd.x * lx + _launcherFwd.y * ly + _launcherFwd.z * lz) / range, -1, 1);
    const gimbalCos = Math.cos(ENEMY_MISSILE.gimbalLimitDeg * DEG2RAD);
    if (gimbalDot < gimbalCos) return;

    // 2) Aspect in player frame, horizontal projection: dot of player
    //    heading direction with (player → launcher). vx/vz is the player's
    //    ground-track direction from _playerTrackDir (low-speed fallback
    //    included). +1 = launcher straight ahead of player
    //    (head-on); -1 = launcher straight behind (tail chase). Beam
    //    shots (|dot| small) are filtered out.
    const horiz = Math.hypot(lx, lz);
    if (horiz < 1) return;
    const aspectDot = clamp(-(vx * (lx / horiz) + vz * (lz / horiz)), -1, 1);
    //                  ^ player→launcher = -(launcher→player); the negation
    //                  flips the launcher→player components used above.
    const aspectCos = Math.cos(ENEMY_MISSILE.aspectConeDeg * DEG2RAD);
    if (Math.abs(aspectDot) < aspectCos) return;

    platform.tryFireMissileAt(player);
  }

  // ── Helpers ──────────────────────────────────────────────────────────────
  /**
   * The player's ground-track direction (XZ, unit) plus their speed, in a
   * shared scratch object. Below minPlayerSpeed we fall back to the player's
   * body forward — at low speed the velocity vector is dominated by
   * ground-spring jitter and points nowhere useful.
   */
  _playerTrackDir(player) {
    const speed = player.velocity.length();
    _track.speed = speed;
    if (speed > ENEMIES.attack.minPlayerSpeed) {
      _track.x = player.velocity.x / speed;
      _track.z = player.velocity.z / speed;
    } else {
      _playerFwd.set(0, 0, -1).applyQuaternion(player.quaternion);
      const len = Math.hypot(_playerFwd.x, _playerFwd.z) || 1;
      _track.x = _playerFwd.x / len;
      _track.z = _playerFwd.z / len;
    }
    return _track;
  }

  /**
   * Clamp a desired vertical rate against the terrain floor. A plain
   * "below minAltAGL → climb" test is fine at cruise but hopeless in a dive:
   * arresting 90 m/s of sink at verticalAccel costs hundreds of metres. So the
   * floor is raised by the altitude the recovery itself will eat, plus one
   * terrain-sample interval of reaction lag and a fixed margin — an evading
   * MiG pulls out early instead of flying into the hill it was diving past.
   */
  _terrainSafeVR(plane, desiredVR) {
    if (!this._hasTerrainSample) return desiredVR;
    const p = this.params;
    const agl = plane.position.y - this._groundHeight;
    const sink = Math.max(0, -plane.verticalRate);
    const recovery = (sink * sink) / (2 * p.verticalAccel)
      + sink * ENEMIES.terrainQueryInterval
      + ENEMIES.terrainRecoveryMargin;
    if (agl - recovery < p.minAltAGL) return p.maxClimbRate;
    return desiredVR;
  }

  _distToWaypointXZ(plane) {
    if (!this.waypoint) return Infinity;
    const dx = this.waypoint.x - plane.position.x;
    const dz = this.waypoint.z - plane.position.z;
    return Math.sqrt(dx * dx + dz * dz);
  }

  _randomWaypoint() {
    const p = this.params;
    const [minX, minZ] = ENEMIES.worldMin;
    const [maxX, maxZ] = ENEMIES.worldMax;
    const altAGL = clamp(
      p.cruiseAltAGL + rand(-ENEMIES.waypointAltJitter, ENEMIES.waypointAltJitter),
      p.minAltAGL, p.maxAltAGL,
    );
    return {
      x: rand(minX, maxX),
      z: rand(minZ, maxZ),
      altAGL,
    };
  }
}
